/**
 * The recipe box. Photos matter here (PRD 6) — a meal you can see is a meal you
 * actually pick at 5pm on a Tuesday. Tapping a card opens the full recipe; new
 * ones can be typed by hand or read in from a link.
 */
import type { Recipe } from '../../shared/types';
import { qtyLabel } from './mealsApi';
import { EmptyState, MealPhoto, btnPrimary, btnSecondary, cardCls } from './ui';

export default function MealLibrary({
  meals,
  onNew,
  onImport,
  onOpen,
}: {
  meals: Recipe[];
  onNew: () => void;
  onImport: () => void;
  onOpen: (meal: Recipe) => void;
}) {
  if (meals.length === 0) {
    return (
      <EmptyState
        title="Your recipe box is empty"
        body="Add the meals you cook on repeat. List what each one takes and which aisle it lives in — then every week's grocery list writes itself. Have a link? Import it and we'll do the typing."
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <button type="button" className={btnPrimary} onClick={onNew}>
              Add your first meal
            </button>
            <button type="button" className={btnSecondary} onClick={onImport}>
              Import from a link
            </button>
          </div>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-soft">
          {meals.length} meal{meals.length === 1 ? '' : 's'} in the box
        </p>
        <div className="flex gap-2">
          <button type="button" className={btnSecondary} onClick={onImport}>
            Import from a link
          </button>
          <button type="button" className={btnPrimary} onClick={onNew}>
            <span aria-hidden="true">＋</span> New meal
          </button>
        </div>
      </div>

      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {meals.map((meal) => {
          const ingredients = meal.ingredients ?? [];
          const preview = ingredients
            .slice(0, 4)
            .map((i) => [qtyLabel(i.quantity, i.unit), i.name].filter(Boolean).join(' '))
            .join(' · ');
          return (
            <li key={meal.id}>
              <button
                type="button"
                onClick={() => onOpen(meal)}
                className={`${cardCls} flex h-full w-full flex-col overflow-hidden text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage`}
              >
                <MealPhoto name={meal.name} url={meal.photo_url} className="h-36 w-full" />
                <span className="flex flex-1 flex-col gap-1 p-4">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="font-display text-base font-semibold text-ink">{meal.name}</span>
                    {meal.servings !== null && (
                      <span className="shrink-0 text-xs text-ink-faint">Serves {meal.servings}</span>
                    )}
                  </span>
                  <span className="text-xs text-ink-faint">
                    {ingredients.length === 0
                      ? 'No ingredients yet — add them to feed the grocery list'
                      : preview + (ingredients.length > 4 ? ` · +${ingredients.length - 4} more` : '')}
                  </span>
                  {meal.notes && (
                    <span className="mt-1 line-clamp-2 text-xs text-ink-soft">{meal.notes}</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
